import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, of, skipWhile, Subject, take } from 'rxjs';
import { ERRORS, PROCESS_SERVICE_STATES } from '../../../_constants/values/values.service';
import { ApiOrganisationsService, Organisation } from '../../requests/api-organisations/api-organisations.component';

@Injectable({
    providedIn: 'root'
})
export class OrganisationsService {
    readonly onlistOrganisations$: BehaviorSubject<string> = new BehaviorSubject<string>(PROCESS_SERVICE_STATES.waiting);
    private markToUpdateOrganisations = true;

    private organisations: any[] = [];
    private organisationsJson: any[] = [];
    private organisationsChanged$ = new Subject<void>();
    private oneOrgInfo: any = null;
    constructor(
        private readonly apiOrganisationsService: ApiOrganisationsService
    ) { }

    public subscribeToOrganisations() {
        return this.organisationsChanged$.asObservable();
    }

    /**
     * List all organisations
     * @returns The organisations
     */
    public list(): Observable<any> {

        if (!this.markToUpdateOrganisations) {
            return of(this.organisations);
        }

        if (this.onlistOrganisations$.value === PROCESS_SERVICE_STATES.inProgress) {
            return this.onlistOrganisations$.asObservable()
                .pipe(
                    skipWhile((res: any) => res !== PROCESS_SERVICE_STATES.done),
                    take(1)
                );
        } else {
            this.onlistOrganisations$.next(PROCESS_SERVICE_STATES.inProgress);
            return this.apiOrganisationsService.list()
                .pipe(
                    map((resp: any) => {
                        if (resp) {
                            this.organisations = resp;
                            for (const organisation of this.organisations) {
                                this.organisationsJson[organisation.org_id] = organisation;
                            }
                            this.markToUpdateOrganisations = false;
                            this.onlistOrganisations$.next(PROCESS_SERVICE_STATES.done);
                        } else {
                            this.markToUpdateOrganisations = true;
                            this.onlistOrganisations$.next(PROCESS_SERVICE_STATES.done);
                            throw ERRORS.malformedRequest;
                        }
                        return true;
                    }),
                    catchError((error: object) => {
                        this.markToUpdateOrganisations = true;
                        this.onlistOrganisations$.next(PROCESS_SERVICE_STATES.done);
                        throw error;
                    })
                );
        }
    }

    public getOrganisations(): Organisation[] {
        return this.organisations;
    }

    public getOrganisationsJson(): { [key: number]: Organisation } {
        return this.organisationsJson;
    }

    public getOrganisationById(orgId: number): Organisation {
        return this.organisationsJson[orgId] || {} as Organisation;
    }

    public getOneOrgInfo(): any {
        return this.oneOrgInfo;
    }

    public markToUpdate(): void {
        this.markToUpdateOrganisations = true;
    }

    public listOneOrganisation(data: any): Observable<Organisation> {
        return this.apiOrganisationsService.getOne(data)
            .pipe(
                map((resp: Organisation) => {
                    this.organisationsJson[resp.org_id] = resp;
                    for (const organisation of this.organisations) {
                        if (organisation.org_id === resp.org_id) {
                            this.organisations[this.organisations.indexOf(organisation)] = resp;
                            break;
                        }
                    }
                    if (!this.organisations.find(organisation => organisation.org_id === resp.org_id)) {
                        this.organisations.push(resp);
                    }
                    return resp;
                }),
                catchError((error: object) => {
                    throw error;
                })
            );
    }

    public createOrganisation(data: Organisation): Observable<any> {
        return this.apiOrganisationsService.create(data)
            .pipe(
                map((resp: any) => {
                    if (resp) {
                        const newOrganisation = {
                            ...data,
                            ...resp
                        };
                        this.insertOrganisationAlphabetically(newOrganisation);
                        this.organisationsJson[resp.org_id] = newOrganisation;
                        this.organisationsChanged$.next();
                        return resp;
                    } else {
                        return of(ERRORS.malformedRequest);
                    }
                }),
                catchError((error: object) => {
                    throw error;
                })
            );
    }

    public updateOrganisation(data: Organisation): Observable<any> {
        return this.apiOrganisationsService.update(data)
            .pipe(
                map((resp: any) => {
                    const index = this.organisations.findIndex((organisation: any) => organisation.org_id === data.org_id);
                    this.auxCopyOrganisation(this.organisations[index], data);
                    this.auxCopyOrganisation(this.organisationsJson[data.org_id], data);
                    this.markToUpdateOrganisations = false;
                    this.onlistOrganisations$.next(PROCESS_SERVICE_STATES.done);
                    this.organisationsChanged$.next();
                    return resp;
                }),
                catchError((error: object) => {
                    throw error;
                })
            );
    }

    private auxCopyOrganisation(targetOrganisation: Organisation, updatedOrganisation: Organisation): void {
        targetOrganisation.name = updatedOrganisation.name;
        targetOrganisation.city_region_id = updatedOrganisation.city_region_id;
        targetOrganisation.address = updatedOrganisation.address;
        targetOrganisation.recom = updatedOrganisation.recom;
        targetOrganisation.caen_no = updatedOrganisation.caen_no;
    }

    public deleteOrganisation(orgId: number): Observable<any> {
        return this.apiOrganisationsService.delete(orgId)
            .pipe(
                map((resp: any) => {
                    if (resp) {
                        this.organisations = this.organisations.filter((organisation: any) => organisation.org_id !== orgId);
                        delete this.organisationsJson[orgId];
                        this.organisationsChanged$.next();
                        return resp;
                    } else {
                        throw resp;
                    }
                }),
                catchError((error: object) => {
                    throw error;
                })
            );
    }

    private insertOrganisationAlphabetically(newOrganisation: Organisation): void {
        const auxOrganisations = [];
        let inserted = false;
        for (const organisation of this.organisations) {
            if (inserted === false && organisation.name > newOrganisation.name) {
                auxOrganisations.push(newOrganisation);
                inserted = true;
            }
            auxOrganisations.push(organisation);
        }
        if (inserted === false) {
            auxOrganisations.push(newOrganisation);
        }
        this.organisations = auxOrganisations;
    }
}